import { useState, useEffect } from "react";
import { X, Globe, Lock, Check, Star } from "lucide-react";
import type {
  ConversationViewResponse,
  ConversationViewCreateRequest,
} from "../../gen/models";
import { COLOR_OPTIONS, getColorOption } from "../../context/ConversationViewsContext";
import { useAgentOptions } from "../../hooks/useAgentOptions";

interface ViewModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: ConversationViewCreateRequest) => Promise<void>;
  initialView?: ConversationViewResponse | null;
  defaultFilters?: Record<string, any>;
  isSubmitting?: boolean;
}

export function ViewModal({
  isOpen,
  onClose,
  onSubmit,
  initialView,
  defaultFilters,
  isSubmitting = false,
}: ViewModalProps) {
  const [name, setName] = useState("");
  const [color, setColor] = useState("emerald");
  const [isGlobal, setIsGlobal] = useState(false);
  const [isDefault, setIsDefault] = useState(false);
  const [agent, setAgent] = useState("all");
  const agentOptions = useAgentOptions();
  const [status, setStatus] = useState("all");
  const [quickFilter, setQuickFilter] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [validationError, setValidationError] = useState<string | null>(null);

  useEffect(() => {
    if (initialView) {
      setName(initialView.name);
      setColor(initialView.color || "emerald");
      setIsGlobal(initialView.is_global);
      setIsDefault(Boolean(initialView.is_default));
      const f = (initialView.filters || {}) as Record<string, any>;
      setAgent(f.agent || "all");
      setStatus(f.status || "all");
      setQuickFilter(f.quick_filter || f.evaluation || "all");
      setSearchQuery(f.q || "");
    } else {
      setName("");
      setColor("emerald");
      setIsGlobal(false);
      setIsDefault(false);
      if (defaultFilters) {
        setAgent(defaultFilters.agent || "all");
        setStatus(defaultFilters.status || "all");
        setQuickFilter(defaultFilters.quick_filter || defaultFilters.evaluation || "all");
        setSearchQuery(defaultFilters.q || "");
      } else {
        setAgent("all");
        setStatus("all");
        setQuickFilter("all");
        setSearchQuery("");
      }
    }
    setValidationError(null);
  }, [initialView, defaultFilters, isOpen]);

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) {
      setValidationError("View name is required");
      return;
    }

    const filters: Record<string, any> = {};
    if (agent !== "all") filters.agent = agent;
    if (status !== "all") filters.status = status;
    if (quickFilter !== "all") filters.quick_filter = quickFilter;
    if (searchQuery.trim()) filters.q = searchQuery.trim();

    try {
      await onSubmit({
        name: name.trim(),
        color,
        is_global: isGlobal,
        is_default: isDefault,
        filters,
      });
      onClose();
    } catch {
      // handled by context toast
    }
  };

  const selectedColorOption = getColorOption(color);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-stone-900/60 backdrop-blur-xs animate-in fade-in duration-150">
      <div className="relative w-full max-w-md rounded-xl bg-white dark:bg-stone-900 border border-stone-200 dark:border-stone-800 shadow-2xl overflow-hidden flex flex-col max-h-[90vh]">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-stone-200 dark:border-stone-800 bg-stone-50/50 dark:bg-stone-950/40">
          <div className="flex items-center gap-2">
            <div
              className={`w-3.5 h-3.5 rounded-full ${selectedColorOption.bgClass} shadow-xs ring-2 ring-white dark:ring-stone-900`}
            />
            <h3 className="text-sm font-bold text-stone-900 dark:text-stone-100">
              {initialView ? "Edit View" : "New Conversations View"}
            </h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1 rounded-md text-stone-400 hover:text-stone-600 dark:hover:text-stone-200 hover:bg-stone-100 dark:hover:bg-stone-800 transition"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Form Body */}
        <form onSubmit={handleSubmit} className="p-5 overflow-y-auto space-y-4">
          {validationError && (
            <div className="p-2.5 rounded-lg bg-rose-50 dark:bg-rose-950/60 border border-rose-200 dark:border-rose-900 text-rose-700 dark:text-rose-400 text-xs">
              {validationError}
            </div>
          )}

          {/* View Name */}
          <div className="space-y-1.5">
            <label className="block text-xs font-semibold text-stone-700 dark:text-stone-300">
              View Name <span className="text-rose-500">*</span>
            </label>
            <input
              type="text"
              required
              placeholder="e.g. Frontdesk Review Queue"
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                setValidationError(null);
              }}
              className="w-full px-3 py-2 text-xs rounded-lg bg-white dark:bg-stone-900 border border-stone-200 dark:border-stone-800 text-stone-900 dark:text-stone-100 focus:outline-hidden focus:ring-2 focus:ring-stone-400 dark:focus:ring-stone-600 shadow-2xs font-sans"
              autoFocus
            />
          </div>

          {/* Color Label */}
          <div className="space-y-1.5">
            <label className="block text-xs font-semibold text-stone-700 dark:text-stone-300">
              Color Label (Border Highlight)
            </label>
            <div className="flex items-center gap-2 flex-wrap">
              {COLOR_OPTIONS.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => setColor(c.id)}
                  title={c.name}
                  className={`w-7 h-7 rounded-lg ${c.bgClass} flex items-center justify-center transition-all shadow-xs ${
                    color === c.id
                      ? "ring-2 ring-offset-2 ring-stone-900 dark:ring-stone-100 dark:ring-offset-stone-900 scale-105"
                      : "opacity-80 hover:opacity-100 hover:scale-105"
                  }`}
                >
                  {color === c.id && <Check className="w-4 h-4 text-white stroke-[3]" />}
                </button>
              ))}
            </div>
          </div>

          {/* Scope (Global vs Private) */}
          <div className="space-y-1.5">
            <label className="block text-xs font-semibold text-stone-700 dark:text-stone-300">
              Visibility Scope
            </label>
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => setIsGlobal(false)}
                className={`p-2.5 rounded-lg border text-left transition flex items-start gap-2.5 ${
                  !isGlobal
                    ? "border-stone-900 dark:border-stone-100 bg-stone-50 dark:bg-stone-800/80 shadow-2xs"
                    : "border-stone-200 dark:border-stone-800 hover:bg-stone-50 dark:hover:bg-stone-800/40"
                }`}
              >
                <Lock className="w-4 h-4 text-stone-500 dark:text-stone-400 shrink-0 mt-0.5" />
                <div className="min-w-0">
                  <div className="text-xs font-bold text-stone-900 dark:text-stone-100">
                    Private View
                  </div>
                  <div className="text-[10px] text-stone-500 dark:text-stone-400 leading-tight mt-0.5">
                    Visible only to you
                  </div>
                </div>
              </button>

              <button
                type="button"
                onClick={() => setIsGlobal(true)}
                className={`p-2.5 rounded-lg border text-left transition flex items-start gap-2.5 ${
                  isGlobal
                    ? "border-stone-900 dark:border-stone-100 bg-stone-50 dark:bg-stone-800/80 shadow-2xs"
                    : "border-stone-200 dark:border-stone-800 hover:bg-stone-50 dark:hover:bg-stone-800/40"
                }`}
              >
                <Globe className="w-4 h-4 text-stone-500 dark:text-stone-400 shrink-0 mt-0.5" />
                <div className="min-w-0">
                  <div className="text-xs font-bold text-stone-900 dark:text-stone-100">
                    Global View
                  </div>
                  <div className="text-[10px] text-stone-500 dark:text-stone-400 leading-tight mt-0.5">
                    Shared with organization
                  </div>
                </div>
              </button>
            </div>

            {/* Default View Setting */}
            <div className="flex items-center gap-2 pt-1.5">
              <input
                type="checkbox"
                id="view-modal-set-default"
                checked={isDefault}
                onChange={(e) => setIsDefault(e.target.checked)}
                className="w-4 h-4 rounded border-stone-300 dark:border-stone-700 text-stone-900 focus:ring-stone-500 cursor-pointer"
              />
              <label
                htmlFor="view-modal-set-default"
                className="text-xs text-stone-700 dark:text-stone-300 font-medium select-none cursor-pointer flex items-center gap-1.5"
              >
                <Star
                  className={`w-3.5 h-3.5 ${
                    isDefault ? "text-amber-500 fill-amber-400" : "text-stone-400"
                  }`}
                />
                <span>Set as my default view</span>
              </label>
            </div>
          </div>

          {/* Filtering Criteria */}
          <div className="space-y-3 pt-2 border-t border-stone-100 dark:border-stone-800/80">
            <span className="text-[11px] font-bold uppercase tracking-wider text-stone-400 dark:text-stone-500">
              Filter Parameters
            </span>

            {/* Agent Filter */}
            <div className="space-y-1">
              <label className="block text-[11px] font-medium text-stone-600 dark:text-stone-400">
                Agent
              </label>
              <select
                value={agent}
                onChange={(e) => setAgent(e.target.value)}
                className="w-full px-2.5 py-1.5 text-xs rounded-lg bg-white dark:bg-stone-900 border border-stone-200 dark:border-stone-800 text-stone-900 dark:text-stone-100 focus:outline-hidden"
              >
                <option value="all">All Agents</option>
                {agentOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Evaluation Filter */}
            <div className="space-y-1">
              <label className="block text-[11px] font-medium text-stone-600 dark:text-stone-400">
                Evaluation / Review Status
              </label>
              <select
                value={quickFilter}
                onChange={(e) => setQuickFilter(e.target.value)}
                className="w-full px-2.5 py-1.5 text-xs rounded-lg bg-white dark:bg-stone-900 border border-stone-200 dark:border-stone-800 text-stone-900 dark:text-stone-100 focus:outline-hidden"
              >
                <option value="all">All Evaluations</option>
                <option value="unreviewed">Unreviewed</option>
                <option value="low_score">Flagged (Low Score)</option>
                <option value="high_score">High Score</option>
              </select>
            </div>

            {/* Status Filter */}
            <div className="space-y-1">
              <label className="block text-[11px] font-medium text-stone-600 dark:text-stone-400">
                Thread Status
              </label>
              <select
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                className="w-full px-2.5 py-1.5 text-xs rounded-lg bg-white dark:bg-stone-900 border border-stone-200 dark:border-stone-800 text-stone-900 dark:text-stone-100 focus:outline-hidden"
              >
                <option value="all">All Statuses</option>
                <option value="exclude_simulation">Exclude Simulations</option>
                <option value="active">Active</option>
                <option value="completed">Completed</option>
                <option value="simulation">Simulation</option>
                <option value="archived">Archived</option>
              </select>
            </div>

            {/* Search Query */}
            <div className="space-y-1">
              <label className="block text-[11px] font-medium text-stone-600 dark:text-stone-400">
                Keyword / Search Text (Optional)
              </label>
              <input
                type="text"
                placeholder="Leave blank or enter term..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full px-2.5 py-1.5 text-xs rounded-lg bg-white dark:bg-stone-900 border border-stone-200 dark:border-stone-800 text-stone-900 dark:text-stone-100 focus:outline-hidden"
              >
              </input>
            </div>
          </div>

          {/* Visual Sidebar Preview */}
          <div className="pt-2 border-t border-stone-100 dark:border-stone-800/80">
            <span className="text-[10px] font-bold uppercase tracking-wider text-stone-400 dark:text-stone-500 mb-1.5 block">
              Menu Item Preview
            </span>
            <div
              className={`flex items-center justify-between px-3 py-2 rounded-lg bg-stone-100 dark:bg-stone-800/60 border-l-[3.5px] ${selectedColorOption.borderClass} text-xs font-medium text-stone-900 dark:text-stone-100 shadow-2xs`}
            >
              <span className="truncate">{name || "View Name Preview"}</span>
              <div className="flex items-center gap-1.5 shrink-0 text-stone-400">
                {isGlobal ? (
                  <span title="Global view"><Globe className="w-3 h-3" /></span>
                ) : (
                  <span title="Private view"><Lock className="w-3 h-3" /></span>
                )}
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center justify-end gap-2 pt-3 border-t border-stone-100 dark:border-stone-800/80">
            <button
              type="button"
              onClick={onClose}
              disabled={isSubmitting}
              className="px-3 py-1.5 rounded-lg border border-stone-200 dark:border-stone-800 bg-white dark:bg-stone-900 text-xs font-semibold text-stone-700 dark:text-stone-300 hover:bg-stone-50 dark:hover:bg-stone-800 transition"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting || !name.trim()}
              className="px-3.5 py-1.5 rounded-lg bg-stone-900 dark:bg-stone-100 text-white dark:text-stone-900 text-xs font-semibold hover:bg-stone-800 dark:hover:bg-white transition shadow-2xs disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? "Saving..." : initialView ? "Update View" : "Create View"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
